import { Timestamp } from "firebase/firestore";

export type PrevSong = {
  songId: string;
  artistName: string;
  artistIdList: string[];
  title: string;
  thumbnailUrl: string;
  youtubeId: string;
  viewCount: number;
  chartRank: number;
  likeUserIdList: string[];
  isAI: boolean;
  makerUserId: string;
  madeAt: any;
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
};

export type Toast = {
  id?: string;
  content: string;
  duration?: number;
  type?: "success" | "error"; // type 속성 추가
  bottom?: number;
};

export type AudioPair = {
  mrUrl: string;
  vocalUrl: string;
  pitch?: number;
};

export type BrewingTransaction = {
  transactionId: string;
  timestamp: Timestamp;
  type: string;
  songRequestId: string;
  songId: string;
  songTitle: string;
  artistName: string;
  existingArtist: Artist | null;
  audioPairList: AudioPair[];
  status: "completed" | "failed" | "pending";
  error?: string;
  targetUserInfoList: {
    // 알림을 보내야 할 유저 정보
    userId: string;
    userToken?: string; // FCM 토큰
  }[];
  updatedUserIdList: string[];
  notificationsSent: {
    userId: string;
    status: "completed" | "failed";
    error?: string;
  }[];
};

export type Song = {
  songId: string;
  artist: string;
  artistIdList: string[];
  url: string;
  title: string;
  artistName: string; // 추후 데이터 삭제해야함. 번들 업데이트 유저가 다 됐을 시점에.
  thumbnailUrl: string; // 추후 데이터 삭제해야함. 번들 업데이트 유저가 다 됐을 시점에.
  artwork: string;
  youtubeId: string;
  viewCount: number;
  viewCount30sec: number;
  viewCount50sec: number;
  viewCount60sec: number;
  viewCount90sec: number;
  chartRank: number;
  likeUserIdList: string[];
  tagList: string[];
  searchKeywordList: string[];
  lyricistNameList: string[];
  composerNameList: string[];
  arrangerNameList: string[];
  isKomcaManaged: boolean;
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
  isAI: boolean;
  makerUserId: string;
  madeAt: any;
  originalSongId: string; // 새롭게 추가
  isOriginalSong: boolean; // 새롭게 추가
};

export type OriginalSong = {
  id: string;
  title: string;
  artist: Artist | null;
  artistId: string | null;
  artistName: string;
  songIdList: string[];
  coverArtistIdList: string[]; // 대비용
  guideIdList?: string[]; //
  madeAt: Timestamp;
};

export type Artist = {
  artistId: string;
  artistName: string;
  artistImageUrl: string;
  artistDetailImageUrl: string;
  chartRank: 0;
  followerIdList: string[];
  songIdList: string[];
  shortIdList: string[];
  homebrewPlaylistIdList: string[];
  searchKeywordList: string[];
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
  isMale: boolean;
  modelName: string | null;
};

export type Short = {
  shortId: string;
  artistName: string;
  artistIdList: string[];
  title: string;
  thumbnailUrl: string;
  youtubeId: string;
  viewCount: number;
  viewCount30sec: number;
  chartRank: number;
  likeUserIdList: string[];
  isAI: boolean;
  makerUserId: string;
  originalSongId: string;
  madeAt: any;
  tag: {
    genreList: string[];
    themeList: string[];
    tagList: string[];
    [key: string]: any;
  };
};

export type Playlist = {
  playlistId: string;
  playlistName: string;
  playlistImageUrl: string;
  songList: Song[];
};

export type HomebrewPlaylist = {
  playlistId: string;
  artistIdList: string[];
  artistName: string;
  title: string;
  subTitle: string;
  imageUrl: string;
  songList: Song[];
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
};

export type User = {
  userId: string;
  userName: string;
  email: string;
  searchKeywordList: string[];
  likeSongList: Song[];
  followArtistList: Artist[];
  songRequestList: SongRequest[];
  playlistList: Playlist[];
  fcmToken: string;
  tag: {
    genreList: string[];
    dislikeSongIdList: string[];
    [key: string]: any;
  };
  credit: {
    balance: number;
    referralCode: string;
  };
  notificationList: {
    id: string;
    songId: string;
    type: "NEW_SONG" | "SONG_UPDATE" | "SONG_REQUEST_FAILED";
    isRead: boolean;
    createdAt: any;
  }[];
  recentSongIdList?: string[];
};

export type Device = {
  deviceId: string;
  hashedDeviceId: string;
  fcmToken: string;
  userName: string;
  userId: string;
  email: string;
  platform: string;
  recentlyUseAt: any;
  tag: {
    genreList: string[];
    dislikeSongIdList: string[];
    [key: string]: any;
  };
};

export type SongRequest = {
  songRequestId: string;
  songId: string;
  songTitle: string;
  artistName: string;
  existingArtist: Artist | null; // 추가
  status: Status;
  userId: string;
  deviceId: string;
  hashedDeviceId: string;
  userName: string;
  userEmail: string;
  userToken: string;
  otherUserList: {
    userId: string;
    userName: string;
    userEmail: string;
    userToken: string;
  }[]; // 추가
  requestAt: any;
  guideId?: string;
  originalSongId?: string;
};

export type Token = {
  fcmToken: string;
  userName: string;
  email: string;
  platform?: string;
  recentlyUseAt?: any;
};

export type Guide = {
  id: string;
  guideTitle: string;
  isMale: boolean;
  driveId: string;
  searchKeywordList: string[];
  originalSongId?: string;
};

export type Update = {
  id: string;
  updateDate: Timestamp;
  allUpdateDate: Timestamp;
  updateDocumentId: string;
};

export type ISongLog = {
  songId: string;
  logAt: Date;
};
export type ISearchLog = {
  searchWord: string;
  logAt: Date;
};

export type DeviceLog = {
  deviceId: string;
  likeSongList: ISongLog[];
  playStartList: ISongLog[];
  play30secList: ISongLog[];
  play60secList: ISongLog[];
  play90secList: ISongLog[];
  playCompleteList: ISongLog[];
  searchQueryList: ISearchLog[];
};

export type PushNotificationLog = {
  id: string;
  successfulTokenList: string[];
  failedTokenList: string[];
  pushAt: any;
};

export enum Genre {
  BALLAD = "ballad",
  INDIE = "indie",
  ROCK = "rock",
  BLUES = "blues",
  JAZZ = "jazz",
  RNB = "rnb",
  FOLK = "folk",
  POP = "pop",
  KPOP = "kpop",
  JPOP = "jpop",
  DANCE = "dance",
  HIPHOP = "hiphop",
  OST = "ost",
}

export enum Theme {
  라이브 = "live",
  커버 = "cover",
  AI = "ai",
  음원 = "song",
  기타 = "other",
}

export enum Collections {
  USER = "User",
  SONG = "Song",
  SHORT = "Short",
  ARTIST = "Artist",
  SONG_REQUEST = "SongRequest",
  DEVICE = "Device",
  HOMEBREW_PLAYLIST = "HomebrewPlaylist",
  TESTER = "Tester",
  TOKEN = "Token",
  DEVICE_LOG = "DeviceLog",
  STAGING_TOKEN = "StagingToken",
  DEBUG_TOKEN = "DebugToken",
  UPDATE = "Update",
  MAKING_REQUEST = "MakingRequest",
  CREDIT_TRANSACTION = "CreditTransaction",
  BREWING_TRANSACTION = "BrewingTransaction",
  AUTO_BREWING_TRANSACTION = "AutoBrewingTransaction",
  PENDING_BREWING_TRANSACTION = "PendingBrewingTransaction",
  PUSH_NOTIFICATION_LOG = "PushNotificationLog",
  GUIDE = "Guide",
  ORIGINAL_SONG = "OriginalSong",
  REPAIR_REQUEST = "RepairRequest",
  ONBOARDING_ACQUISITION = "OnboardingAcquisition",
  ATTRIBUTION = "attribution",
  USER_ACQUISITION = "UserAcquisition",
  USER_SONG_REQUEST = "UserSongRequest",
  MIGRATION_LOG = "MigrationLog",
}

export enum Status {
  PENDING = "pending",
  EXISTING = "existing",
  READY = "ready",
  COMPLETE = "completed",
  MIDDLE_COMPLETED = "middleCompleted",
  FAILED = "failed",
  ERROR = "error",
}

export type Tag = {
  key1: string;
};

export type PushType = "제조완료" | "곡업데이트";

export type MakingRequest = {
  songRequestId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userToken: string;
  songTitle: string;
  artistName: string;
  existingArtist: Artist | null;
  thumbnailUrl: string;
  otherUserList: {
    userId: string;
    userName: string;
    userEmail: string;
    userToken: string;
  }[]; // 추가된 필드
  status: Status; // 추가된 필드
};

export type PushScreen = "Recent" | "MyBrewingList" | "Home" | "My";

export type PendingBrewingTransaction = {
  id: string;
  songRequestId: string;
  existingArtist: Artist | null;
  status: "completed" | "failed" | "pending";
  error?: string;
  userId: string;
  userName: string;
  userToken: string;
  model: string;
  isMaleSinger: boolean;
  guideTitle: string;
  guideId: string;
  requestAt: any;
  [key: string]: any;
};

export type MigrationLog = {
  id: string;
  userId: string;
  status: "pending" | "completed" | "failed";
  oldRequestCount: number;
  newRequestCount: number;
  startedAt: any;
  completedAt?: any;
  error?: string;
};

export type CreditTransaction = {
  id: string;
  userId?: string;
  deviceId: string;
  amount: number;
  type: CreditTransactionType; // 대분류
  action: CreditActionType; // 소분류
  status: CreditTransactionStatus;
  metadata: {
    referralCode?: string;
    referrerUserId?: string;
    shareTarget?: string;
    shareCount?: number;
    contentId?: string;
    contentType?: string;
    platform?: string;
    appVersion?: string;
    [key: string]: any;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

export enum CreditTransactionType {
  EARN = "earn", // 크레딧 획득
  SPEND = "spend", // 크레딧 사용
  SYSTEM = "system", // 시스템 작업 (조정, 만료 등)
}

export enum CreditActionType {
  // 획득 액션
  REFERRAL_CODE_USE = "REFERRAL_CODE_USE", // 추천코드 사용
  REFERRAL_SIGNUP = "REFERRAL_SIGNUP", // 추천인 가입 완료
  SHARE_MILESTONE = "SHARE_MILESTONE", // 공유 마일스톤
  DAILY_LOGIN = "DAILY_LOGIN", // 일일 로그인
  LISTEN_COMPLETE = "LISTEN_COMPLETE", // 곡 완청
  PURCHASE = "PURCHASE", // 곡 완청
  WATCH_AD = "WATCH_AD", // 광고 시청

  // 사용 액션
  SONG_REQUEST = "song_request", // 곡 요청
  CREATE_PLAYLIST = "create_playlist", // 플레이리스트 생성

  // 시스템 액션
  SYSTEM_ADJUST = "system_adjust", // 시스템 조정
  EXPIRE = "expire", // 만료
  REFUND = "refund", // 환불/취소
}

export enum CreditTransactionStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export type RepairRequest = {
  songId: string;
  userId: string | null;
  deviceId: string | null;
  reasons: string[];
  status: "pending";
  createdAt: any;
};

export type FirstOption = {
  id: string;
  title: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
};

export type SecondOption = {
  id: string;
  firstOptionId: string;
  title: string;
  thumbnailUrl: string;
  order: number;
  isActive: boolean;
};

export type OnboardingAcquisition = {
  id: string;
  version: number;
  lastUpdated: any;
  options: {
    first: FirstOption[];
    second: SecondOption[];
  };
};
